import { useLocation, useNavigate } from "react-router-dom";
import { useGetWorkout } from "../../api/hooks/useWorkout";
import { Box, CircularProgress, Button, Table, TableBody, TableCell, TableRow } from "@mui/material";
import { format } from "date-fns";
import { WORKOUT_DURATION, DIFFICULTY_LEVEL, VISIBILITY_SCOPE } from "../../constants/settings";

const ViewWorkout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const workoutId = (location.state as any)?.workoutId || "";

    const { data, isLoading, error } = useGetWorkout(workoutId, { enabled: !!workoutId });

    const workout = (data?.data || {}) as any;

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        try {
            return format(new Date(dateString), "dd MMM yyyy");
        } catch {
            return "N/A";
        }
    };

    const formatValue = (val: any) => {
        if (!val) return "N/A";
        if (typeof val === "string") return val;
        if (typeof val === "object") return val.name || val._id || "N/A";
        return String(val);
    };

    const formatArray = (arr?: any[]) => {
        if (!arr || !arr.length) return "N/A";
        return arr
            .map((item) => (typeof item === "string" ? item : item?.name || item?._id || ""))
            .filter(Boolean)
            .join(", ");
    };
    const formatDurationFromEnum = (val?: number) => {
        if (val === WORKOUT_DURATION.FIVE_MINS) return "5 Minutes";
        if (val === WORKOUT_DURATION.TEN_MINS) return "10 Minutes";
        if (val === WORKOUT_DURATION.TWENTY_MINS) return "20 Minutes";
        if (val === WORKOUT_DURATION.THIRTY_MINS) return "30 Minutes";
        if (val === WORKOUT_DURATION.FORTY_MINS) return "40 Minutes";
        if (val === WORKOUT_DURATION.SIXTY_MINS) return "60 Minutes";
        return "N/A";
    };

    const formatDurationFromSeconds = (seconds?: number) => {
        const totalSecs = Number(seconds);
        if (!Number.isFinite(totalSecs)) return "—";

        const safeTotal = Math.max(0, Math.floor(totalSecs));
        const mins = Math.floor(safeTotal / 60);
        const secs = safeTotal % 60;
        return `${mins} m ${secs} s`;
    };

    const formatDuration = (durationInMins?: number, durationEnum?: number) => {
        if (durationInMins !== undefined && durationInMins !== null && Number.isFinite(Number(durationInMins))) {
            return formatDurationFromSeconds(durationInMins);
        }
        return formatDurationFromEnum(durationEnum);
    };

    const formatDifficulty = (val?: number | number[]) => {
        const toCodes = (v: any) => {
            if (Array.isArray(v)) return v.map((x) => Number(x)).filter(Number.isFinite);
            const n = Number(v);
            return Number.isFinite(n) ? [n] : [];
        };

        const codes = toCodes(val);
        if (!codes.length) return "N/A";

        const labels = codes
            .map((code) => {
                if (code === DIFFICULTY_LEVEL.BEGGINER) return "Beginner";
                if (code === DIFFICULTY_LEVEL.BEGINNER_INTERMEDIATE) return "Beginner-Intermediate";
                if (code === DIFFICULTY_LEVEL.INTERMEDIATE) return "Intermediate";
                if (code === DIFFICULTY_LEVEL.ADVANCED) return "Advanced";
                return null;
            })
            .filter(Boolean) as string[];

        return labels.length ? labels.join(", ") : "N/A";
    };
    const formatVisibility = (val?: number) => {
        if (val === VISIBILITY_SCOPE.DEMAND) return "Demand";
        if (val === VISIBILITY_SCOPE.PROGRAM) return "Program";
        if (val === VISIBILITY_SCOPE.BOTH) return "Both";
        return "N/A";
    };

    return (
        <>
            <div className="main_title">
                <h1>Workout Details</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                    <button onClick={() => navigate("/manage-workout")}>Manage Workout</button> - View Workout
                </p>
            </div>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                    <p>Failed to load workout.</p>
                    <Button onClick={() => navigate("/manage-workout")} style={{ marginLeft: 10 }}>
                        Back
                    </Button>
                </Box>
            ) : (
                <div className="cards">
                    <div className="table_container">
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell>Thumbnail</TableCell>
                                    <TableCell>
                                        {workout.thumnail ? (
                                            <img
                                                src={workout.thumnail}
                                                alt="thumb"
                                                style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8 }}
                                            />
                                        ) : (
                                            "N/A"
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Title</TableCell>
                                    <TableCell>{workout.title || "N/A"}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>{formatDuration(workout.durationInMins, workout.duration)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Level</TableCell>
                                    <TableCell>{formatDifficulty(workout.difficultyLevel)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Main Program</TableCell>
                                    <TableCell>{workout.mainProgramId?.title || workout.mainProgramId || "N/A"}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Programs</TableCell>
                                    <TableCell>{formatArray(workout.programRefId)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Visibility</TableCell>
                                    <TableCell>{formatVisibility(workout.visibilityScope)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Equipment</TableCell>
                                    <TableCell>{formatArray(workout.equipment)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Muscle Group</TableCell>
                                    <TableCell>{formatArray(workout.muscleGroup)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Workout Type</TableCell>
                                    <TableCell>{formatArray(workout.trainingStyle)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Goal</TableCell>
                                    <TableCell>{formatArray(workout.goalOutcome)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell style={{ verticalAlign: "top", width: "200px" }}>Short Description</TableCell>
                                    <TableCell style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                                        {workout.shortDescription || "N/A"}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell style={{ verticalAlign: "top", width: "200px" }}>Description</TableCell>
                                    <TableCell style={{ maxWidth: "600px", wordWrap: "break-word" }}>
                                        {workout.description ? (
                                            <div
                                                className="rich-content"
                                                dangerouslySetInnerHTML={{ __html: workout.description }}
                                            />
                                        ) : (
                                            "N/A"
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell style={{ verticalAlign: "top", width: "200px" }}>Exercises</TableCell>
                                    <TableCell style={{ maxWidth: "600px", wordWrap: "break-word" }}>
                                        {workout.excercise || workout.exercises ? (
                                            <div
                                                className="rich-content"
                                                dangerouslySetInnerHTML={{
                                                    __html: (workout.excercise || workout.exercises) as string,
                                                }}
                                            />
                                        ) : (
                                            "N/A"
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Video URL</TableCell>
                                    <TableCell style={{ wordBreak: "break-all" }}>
                                        {workout.videoUrl ? (
                                            /^https?:\/\//i.test(String(workout.videoUrl).trim()) ? (
                                                <a
                                                    href={workout.videoUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: "#1976d2",
                                                        textDecoration: "underline"
                                                    }}
                                                >
                                                    Go To Video
                                                </a>
                                            ) : (
                                                <span style={{ wordBreak: "break-all" }}>
                                                    {String(workout.videoUrl).trim()}
                                                </span>
                                            )
                                        ) : (
                                            "N/A"
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Time stamp</TableCell>
                                    <TableCell>
                                        {(() => {
                                            const rawBeepSecs = Array.isArray(workout.beepSecs) ? workout.beepSecs : [];
                                            if (rawBeepSecs.length > 0) {
                                                const labels = rawBeepSecs.map((s: number) => {
                                                    const mins = Math.floor(s / 60);
                                                    const secs = s % 60;
                                                    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
                                                });
                                                return labels.join(", ");
                                            }
                                            return "N/A";
                                        })()}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Featured</TableCell>
                                    <TableCell>{workout.isFeatured ? "Yes" : "No"}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Published</TableCell>
                                    <TableCell>{workout.isPublished ? "Yes" : "No"}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Comments Count</TableCell>
                                    <TableCell>{workout.commentsCount != null ? workout.commentsCount : "N/A"}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Views</TableCell>
                                    <TableCell>{workout.views != null ? workout.views : 0}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Created At</TableCell>
                                    <TableCell>{formatDate(workout.createdAt)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}
        </>
    );
};

export default ViewWorkout;
